import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    CreditCardOutlined,
    BankOutlined,
    WalletOutlined,
    ArrowLeftOutlined,
    CheckCircleFilled,
    SafetyCertificateOutlined,
    ShoppingOutlined,
    LoadingOutlined
} from '@ant-design/icons';
import { ordersService } from '../../services';
import { Order } from '../../types/order.type';
import { redirectToCheckout } from '../../services/stripe.service';
import { redirectToMercadoPago } from '../../services/mercadopago.service';

// OXXO icon inline SVG
const OxxoIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
        <rect x="1" y="4" width="22" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
);

type PaymentMethodId = 'card' | 'oxxo' | 'spei' | 'mercadopago';

interface PaymentMethod {
    id: PaymentMethodId;
    name: string;
    provider: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    activeBorder: string;
    activeRing: string;
    checkColor: string;
    badge?: string;
    features: string[];
    unavailable?: boolean;
    unavailableReason?: string;
}

const PaymentSelection: React.FC = () => {
    const navigate = useNavigate();
    const { orderId } = useParams<{ orderId: string }>();
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethodId | null>(null);
    const [order, setOrder] = useState<Order | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        const fetchOrder = async () => {
            if (!orderId) {
                setError('No se proporcionó un ID de orden');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const orderData = await ordersService.getById(orderId);
                setOrder(orderData);
            } catch (err: any) {
                setError(err?.message || 'Error al cargar la orden');
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [orderId]);

    const isVMType = (type: string | undefined): boolean => {
        if (!type) return false;
        const t = type.toLowerCase();
        return t === 'virtual-machine' || t === 'vm' || t === 'compute';
    };

    const getServiceName = (order: Order): string => {
        const m = order.metadata;
        if (!m) return 'Service';
        if (isVMType(m.type)) return `Virtual Machine - ${m.vmConfig?.osName || 'VM'}`;
        if (m.type === 'database') return `${m.databaseConfig?.engine || 'Database'} ${m.databaseConfig?.version || ''}`;
        if (m.type === 'blueprint' || m.type === 'app-server') return m.stackConfig?.name || 'App Server';
        if (m.type === 'site') return m.siteConfig?.name || 'Site';
        if (m.type === 'deploy-app') return m.appConfig?.name || 'Deploy App';
        return 'Service';
    };

    const getServiceType = (order: Order): string => {
        const m = order.metadata;
        if (!m) return 'Service';
        if (isVMType(m.type)) return 'Virtual Machine';
        const map: Record<string, string> = {
            database: 'Database',
            blueprint: 'App Server',
            'app-server': 'App Server',
            site: 'Site',
            'deploy-app': 'Deploy App',
        };
        return map[m.type as string] || 'Service';
    };

    const getServiceDescription = (order: Order): string => {
        const m = order.metadata;
        if (!m) return '';
        if (isVMType(m.type)) return `VM ${m.vmConfig?.name || ''} con ${m.vmConfig?.instanceType || ''}`;
        if (m.type === 'database') return `Database ${m.databaseConfig?.name || ''} con ${m.databaseConfig?.engine || ''} ${m.databaseConfig?.version || ''} (${m.databaseConfig?.instanceType || ''})`;
        if (m.type === 'blueprint' || m.type === 'app-server') return m.stackConfig?.description || 'App Server';
        if (m.type === 'site') return m.siteConfig?.description || 'Site';
        if (m.type === 'deploy-app') return m.appConfig?.description || 'Deploy App';
        return '';
    };

    // order.total ya viene convertido a pesos desde orders.service.ts
    const amountMXN = order ? order.total : 0;

    const orderDetails = order
        ? {
            serviceName: getServiceName(order),
            serviceType: getServiceType(order),
            description: getServiceDescription(order),
            amount: amountMXN,
            currency: order.currency || 'MXN',
            orderId: order.id,
        }
        : { serviceName: 'Cargando...', serviceType: '', description: '', amount: 0, currency: 'MXN', orderId: '' };

    // OXXO: Stripe limita a $10,000 MXN por transacción
    const OXXO_MAX_MXN = 10_000;
    const oxxoAvailable = !order || amountMXN <= OXXO_MAX_MXN;

    const paymentMethods: PaymentMethod[] = [
        {
            id: 'card',
            name: 'Tarjeta',
            provider: 'Stripe',
            description: 'Crédito o débito — cargo inmediato',
            icon: <CreditCardOutlined className="text-2xl" />,
            color: 'from-indigo-500 to-purple-600',
            activeBorder: 'border-indigo-500',
            activeRing: 'ring-indigo-100',
            checkColor: 'text-indigo-500',
            badge: 'Más popular',
            features: ['Confirmación inmediata', 'Visa, Mastercard, Amex', 'Sin cargos extra'],
        },
        {
            id: 'oxxo',
            name: 'OXXO',
            provider: 'Efectivo · Stripe',
            description: 'Paga en efectivo en cualquier OXXO',
            icon: <OxxoIcon className="w-6 h-6" />,
            color: 'from-red-500 to-orange-500',
            activeBorder: 'border-red-500',
            activeRing: 'ring-red-100',
            checkColor: 'text-red-500',
            features: ['Pago en efectivo', 'Válido 3 días', 'Recibes ficha de pago en pantalla'],
            unavailable: !oxxoAvailable,
            unavailableReason: `Límite de OXXO: $${OXXO_MAX_MXN.toLocaleString()} MXN por transacción`,
        },
        {
            id: 'spei',
            name: 'SPEI',
            provider: 'Transferencia bancaria · Stripe',
            description: 'Transferencia desde cualquier banco mexicano',
            icon: <BankOutlined className="text-2xl" />,
            color: 'from-emerald-500 to-teal-600',
            activeBorder: 'border-emerald-500',
            activeRing: 'ring-emerald-100',
            checkColor: 'text-emerald-500',
            features: ['CLABE generada en pantalla', 'Procesamiento en minutos', 'Sin comisión bancaria'],
        },
        {
            id: 'mercadopago',
            name: 'Mercado Pago',
            provider: 'Mercado Pago',
            description: 'Saldo de cuenta, tarjeta o meses sin intereses',
            icon: <WalletOutlined className="text-2xl" />,
            color: 'from-blue-400 to-blue-600',
            activeBorder: 'border-blue-500',
            activeRing: 'ring-blue-100',
            checkColor: 'text-blue-500',
            features: ['Saldo de cuenta', 'Meses sin intereses', 'Protección al comprador'],
        },
    ];

    const handleContinue = async () => {
        if (!selectedMethod || !order || !orderId) return;
        try {
            setProcessing(true);
            setError(null);
            const currency = order.currency || 'MXN';

            if (selectedMethod === 'mercadopago') {
                await redirectToMercadoPago({
                    productId: orderId,
                    productName: orderDetails.serviceName,
                    price: orderDetails.amount.toString(),
                    currency,
                    quantity: 1,
                    orderId,
                });
                return;
            }

            // card, oxxo, spei → Stripe con método específico
            await redirectToCheckout({
                productId: orderId,
                productName: orderDetails.serviceName,
                price: orderDetails.amount.toString(),
                currency,
                quantity: 1,
                orderId,
                billingInterval: order.metadata?.billingInterval,
                customerEmail: order.metadata?.customerEmail,
                preferredMethod: selectedMethod as 'card' | 'oxxo' | 'spei',
            });
        } catch (err: any) {
            setError(err?.message || 'Error al procesar el pago');
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <LoadingOutlined className="text-4xl text-gray-400 mb-4" spin />
                    <p className="text-gray-600 dark:text-gray-400">Cargando información de la orden...</p>
                </div>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 mb-4">{error || 'No se pudo cargar la orden'}</p>
                    <button onClick={() => navigate(-1)} className="px-6 py-2 bg-black text-white dark:bg-white dark:text-black rounded-lg hover:bg-gray-800">
                        Volver
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <button onClick={() => navigate(-1)} className="group flex items-center gap-2 text-gray-500 hover:text-black mb-4 transition-colors">
                        <div className="w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center group-hover:border-black dark:group-hover:border-white transition-colors">
                            <ArrowLeftOutlined className="text-sm" />
                        </div>
                        <span className="text-sm font-medium">Volver</span>
                    </button>
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight mb-2">Checkout</h1>
                        <div className="text-sm text-gray-500">
                            <span className="text-gray-400">Order ID:</span>{' '}
                            <span className="font-mono font-semibold text-gray-700 dark:text-gray-300 break-all">{order.id}</span>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Left: Payment Methods */}
                    <div className="flex-1">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                                <WalletOutlined /> ¿Cómo quieres pagar?
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                Elige tu método de pago. Serás redirigido a la pasarela correspondiente.
                            </p>

                            <div className="grid grid-cols-1 gap-4">
                                {paymentMethods.map((method) => (
                                    <button
                                        key={method.id}
                                        onClick={() => !method.unavailable && setSelectedMethod(method.id)}
                                        disabled={method.unavailable}
                                        title={method.unavailable ? method.unavailableReason : undefined}
                                        className={`group relative flex items-center p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                                            method.unavailable
                                                ? 'border-gray-100 dark:border-gray-700 opacity-40 cursor-not-allowed'
                                                : selectedMethod === method.id
                                                    ? `${method.activeBorder} bg-gray-50 dark:bg-gray-700/50 ring-2 ${method.activeRing}`
                                                    : 'border-gray-100 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/30'
                                        }`}
                                    >
                                        {/* Icon */}
                                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${method.color} flex items-center justify-center text-white shadow-md mr-4 flex-shrink-0`}>
                                            {method.icon}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <h3 className="font-bold text-gray-900 dark:text-white">{method.name}</h3>
                                                {method.badge && !method.unavailable && (
                                                    <span className="px-2 py-0.5 bg-black text-white dark:bg-white dark:text-black text-[10px] font-bold rounded-full">
                                                        {method.badge}
                                                    </span>
                                                )}
                                                {method.unavailable && (
                                                    <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[10px] font-bold rounded-full">
                                                        No disponible
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">{method.provider}</p>
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                                {method.unavailable ? method.unavailableReason : method.description}
                                            </p>
                                        </div>

                                        {/* Features — visible only when selected */}
                                        {selectedMethod === method.id && !method.unavailable && (
                                            <div className="hidden sm:flex flex-col gap-1 ml-4 mr-4">
                                                {method.features.map((f, i) => (
                                                    <span key={i} className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap flex items-center gap-1">
                                                        <span className={`w-1 h-1 rounded-full flex-shrink-0 ${method.checkColor} bg-current`} />
                                                        {f}
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        {/* Selector */}
                                        {!method.unavailable && (
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                                                selectedMethod === method.id
                                                    ? `border-transparent ${method.checkColor}`
                                                    : 'border-gray-300 dark:border-gray-600'
                                            }`}>
                                                {selectedMethod === method.id && <CheckCircleFilled className="text-lg" />}
                                            </div>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* OXXO hint */}
                            {selectedMethod === 'oxxo' && (
                                <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl text-sm text-orange-800 dark:text-orange-300">
                                    Recibirás una ficha de pago con código de barras válida por 3 días. Puedes pagarla en cualquier tienda OXXO.
                                </div>
                            )}

                            {/* SPEI hint */}
                            {selectedMethod === 'spei' && (
                                <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl text-sm text-emerald-800 dark:text-emerald-300">
                                    Recibirás una CLABE interbancaria. Haz la transferencia desde tu banca en línea — se confirma en minutos.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right: Order Summary */}
                    <div className="lg:w-[380px]">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sticky top-6">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                                <ShoppingOutlined /> Resumen
                            </h2>

                            <div className="mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-gray-500">Order ID</span>
                                    <span className="font-mono font-semibold text-gray-700 dark:text-gray-300 truncate ml-2">{order.id}</span>
                                </div>
                            </div>

                            <div className="space-y-4 mb-6">
                                <div className="flex justify-between items-start pb-4 border-b border-gray-100 dark:border-gray-700">
                                    <div>
                                        <h3 className="font-bold text-gray-900 dark:text-white">{orderDetails.serviceName}</h3>
                                        <p className="text-sm text-gray-500">{orderDetails.serviceType}</p>
                                    </div>
                                    <span className="font-bold text-gray-900 dark:text-white ml-4">${orderDetails.amount}</span>
                                </div>

                                {orderDetails.description && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{orderDetails.description}</p>
                                )}
                            </div>

                            <div className="flex justify-between items-center mb-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                                <span className="text-lg font-bold text-gray-900 dark:text-white">Total</span>
                                <div className="text-right">
                                    <span className="text-2xl font-extrabold text-gray-900 dark:text-white">${orderDetails.amount.toFixed(2)}</span>
                                    <span className="text-xs text-gray-500 block">{orderDetails.currency}</span>
                                </div>
                            </div>

                            {error && (
                                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-600">{error}</p>
                                </div>
                            )}

                            <button
                                onClick={handleContinue}
                                disabled={!selectedMethod || processing}
                                className="w-full py-4 bg-black text-white dark:bg-white dark:text-black rounded-xl font-bold text-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg flex items-center justify-center gap-2"
                            >
                                {processing ? (
                                    <>
                                        <LoadingOutlined spin />
                                        <span>Procesando...</span>
                                    </>
                                ) : selectedMethod ? (
                                    `Pagar $${orderDetails.amount.toFixed(2)} con ${paymentMethods.find(m => m.id === selectedMethod)?.name}`
                                ) : (
                                    'Selecciona un método'
                                )}
                            </button>

                            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-400">
                                <SafetyCertificateOutlined className="text-green-500" />
                                <span>Pago seguro con cifrado SSL</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentSelection;
